import { cx } from 'cva'
import Image from 'next/image'
import Link from 'next/link'
import type { FC } from 'react'
import type { ImageData, Maybe, PhaseTagType } from '../../frontend-types'
import { CarouselCardImages } from '../molecules/CarouselCardImages'
import { Corner } from './Corner'
import { Grid } from './Grid'
import { IconCircle } from './IconCircle'
import { PhaseTag } from './PhaseTag'
import { PlaceholderImage } from './PlaceholderImage'
import { Tag } from './Tag'
import { TextAreaContent } from './TextAreaContent'

export type CardFeatureProps = {
	type: 'venue' | 'news' | 'cta'
	title: string
	category?: Maybe<string>
	subtitle?: string
	excerpt: Maybe<string>
	images?: ImageData[]
	date?: string
	url: string
	phaseTag?: PhaseTagType
	target?: '_self' | '_blank'
	ctaLabel?: string
}

export const CardFeature: FC<CardFeatureProps> = ({
	type,
	title,
	category,
	subtitle,
	excerpt,
	images,
	date,
	url,
	phaseTag,
	ctaLabel,
	target,
}) => {
	const imagesSizes =
		'(max-width: 767px) 100vw, (max-width: 1023px) 50vw, (max-width: 1279px) 58.33vw, (max-width: 1559px) 66.67vw, 936px'

	return (
		<Link href={url} target={target} className="group">
			<Grid className="relative tablet-p:bg-blue-tint-10 tablet-p:rounded-br-sm overflow-hidden">
				<Corner className="text-white" />
				<div
					className={cx(
						'col-span-full bg-blue-tint-10 p-4xl tablet-p:bg-transparent relative',
						'row-start-2 tablet-p:col-span-4 tablet-p:row-start-1 tablet-l:col-span-5 desktop:col-span-4',
					)}
				>
					<div className="flex flex-col justify-between gap-y-4xl relative z-10 h-full">
						<div>
							{category && <Tag label={category} className="mb-lg" />}
							<span className="block text-h3 text-blue layout-narrow:text-h4 group-hover:text-blue-shade-40 group-active:text-blue-shade-70">
								{title}
							</span>
							{subtitle && <span className="mt-sm block text-h5">{subtitle}</span>}
							<TextAreaContent
								className="mt-lg text-blue-shade-70"
								textSize="text-body-sm"
								layoutNarrowTextSize="layout-narrow:text-body-xs"
								text={excerpt}
							/>
						</div>
						<div className="flex items-center justify-between gap-x-sm">
							{type === 'news' && date && (
								<span className="block text-body-sm layout-narrow:text-body-xs">{date}</span>
							)}
							{type === 'cta' && (
								<span className="block text-button text-blue group-hover:text-blue-shade-40 group-active:text-blue-shade-70">
									{ctaLabel}
								</span>
							)}
							{type === 'venue' && phaseTag && <PhaseTag phaseTag={phaseTag} cardSize="large" />}
							<IconCircle icon={['fas', 'chevron-right']} size="responsive" />
						</div>
					</div>
					<div
						className="absolute left-0 top-0 bottom-0 bg-blue-tint-20 opacity-0 group-hover:opacity-100 tablet-p:opacity-100 tablet-p:w-0 tablet-p:group-hover:w-[calc(100%+var(--spacing-gutter))] transition-all ease-in-out duration-300 pointer-events-none z-0"
						aria-hidden="true"
					/>
					<div
						className={cx(
							'absolute right-0 bottom-0 h-(length:--stroke-lg) bg-blue left-0 pointer-events-none transition-all ease-in-out duration-150 z-15',
							'tablet-p:bottom-0 tablet-p:w-(length:--stroke-lg) tablet-p:!h-full tablet-p:group-hover:w-(length:--stroke-xl)',
							'group-hover:bg-blue-shade-40 group-active:bg-blue-shade-70 ',
						)}
						aria-hidden="true"
					/>
				</div>
				<div className="col-span-full row-start-1 tablet-p:col-span-4 tablet-l:col-span-7 desktop:col-span-8 rounded-tl-sm tablet-p:rounded-tl-none overflow-hidden">
					{!images || images.length === 0 ? (
						<div className="aspect-card-feature h-full w-full">
							<PlaceholderImage />
						</div>
					) : images.length > 1 ? (
						<CarouselCardImages
							images={images}
							imageAspectRatio="card-feature"
							imagesSizes={imagesSizes}
						/>
					) : (
						<div
							className="relative aspect-card-feature h-full w-full overflow-hidden"
							aria-hidden="true"
						>
							<Image
								src={images[0].src}
								alt={images[0].alt}
								fill
								className="object-cover group-hover:scale-105 transition-all ease-in-out duration-300"
								sizes={imagesSizes}
							/>
						</div>
					)}
				</div>
			</Grid>
		</Link>
	)
}
